
import React, { useState, useEffect } from 'react';
import { UserRole } from '../types.ts';

interface TourStep {
  title: string;
  content: string;
  targetTab: string;
  icon: string;
}

interface SageGuidedTourProps {
  role: UserRole;
  setActiveTab: (tab: string) => void;
  onComplete: () => void;
}

const SageGuidedTour: React.FC<SageGuidedTourProps> = ({ role, setActiveTab, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isThinking, setIsThinking] = useState(false);

  const studentSteps: TourStep[] = [
    {
      title: "Welcome to CoffeeConnex!",
      content: "I'm Sage. I'll walk you through the platform to help you find professionals in your desired industry and build valuable connections.",
      targetTab: "professionals",
      icon: "fa-hand-wave"
    },
    {
      title: "Find Professionals",
      content: "Start here to find industry experts. I highlight professionals from top companies who align with your specific career goals.",
      targetTab: "professionals",
      icon: "fa-users"
    },
    {
      title: "Campus Peers",
      content: "Toggle to 'Campus Peers' to connect with students at your university. Find study partners or upper-year mentors with internship experience.",
      targetTab: "professionals",
      icon: "fa-user-graduate"
    },
    {
      title: "Messages",
      content: "Manage all your professional conversations here. Keep track of your handshakes and networking history in one focused place.",
      targetTab: "chats",
      icon: "fa-comments"
    },
    {
      title: "Meeting Prep",
      content: "Never go into a chat unprepared. I generate personalized prep guides, research, and questions for every confirmed meeting.",
      targetTab: "prep",
      icon: "fa-clipboard-check"
    },
    {
      title: "Auto Follow-up",
      content: "I'll handle the follow-ups. Set up automated messages to stay top-of-mind with your new connections without the manual effort.",
      targetTab: "automation",
      icon: "fa-sync-alt"
    },
    {
      title: "Career Planner",
      content: "Plan your future. I'll help you map out a long-term roadmap with specific milestones and skills to reach your target role.",
      targetTab: "strategist",
      icon: "fa-chess-knight"
    },
    {
      title: "Networking Events",
      content: "Discover industry mixers and external events tailored to your major. Perfect for expanding your local or virtual network.",
      targetTab: "events",
      icon: "fa-calendar-alt"
    },
    {
      title: "Campus Events",
      content: "Select the 'Campus Hub' filter to see events happening specifically at your school, like career fairs and club workshops.",
      targetTab: "events",
      icon: "fa-university"
    },
    {
      title: "Job Board",
      content: "Browse roles and internships that match your profile. I prioritize listings where you have strong professional synergy.",
      targetTab: "jobs",
      icon: "fa-briefcase"
    },
    {
      title: "Upgrade Plan",
      content: "Scale your growth. Unlock more monthly connection requests and advanced AI features by choosing a higher membership tier.",
      targetTab: "membership",
      icon: "fa-credit-card"
    },
    {
      title: "Your Profile",
      content: "Keep your identity updated. Your goals and interests help me find the highest-fidelity professional matches for you.",
      targetTab: "profile",
      icon: "fa-user-circle"
    }
  ];

  const professionalSteps: TourStep[] = [
    {
      title: "Systems Ready.",
      content: "Hello! I'm Sage. I'll guide you through your console to show you how we manage your professional impact and time efficiently.",
      targetTab: "requests",
      icon: "fa-shield-halved"
    },
    {
      title: "Inbound Requests",
      content: "View incoming connection requests from students. I pre-filter them so you only see high-synergy candidates who meet your criteria.",
      targetTab: "requests",
      icon: "fa-inbox"
    },
    {
      title: "Messages",
      content: "Communicate directly with accepted students. This is where your mentorship and professional exchange takes place.",
      targetTab: "chats",
      icon: "fa-comments"
    },
    {
      title: "Student Insights",
      content: "Discover what you can learn from students. I highlight reverse-mentorship opportunities based on the latest academic research.",
      targetTab: "reciprocity",
      icon: "fa-brain"
    },
    {
      title: "My Schedule",
      content: "Logistics made easy. Sync your calendar to manage your coffee chat cycles and availability in one clean view.",
      targetTab: "calendar",
      icon: "fa-calendar"
    },
    {
      title: "Earn Rewards",
      content: "Your time is valuable. Redeem the points you earn from mentoring for professional development, tech, or charity.",
      targetTab: "rewards",
      icon: "fa-award"
    },
    {
      title: "Career Planner",
      content: "Even pros need a plan. Use our strategy engine to chart your next executive move or technical pivot.",
      targetTab: "strategist",
      icon: "fa-chess-knight"
    },
    {
      title: "Professional Profile",
      content: "Manage your availability and mentorship filters. Set exactly how many connections you want to host each week.",
      targetTab: "profile",
      icon: "fa-user-circle"
    }
  ];

  const steps = role === UserRole.STUDENT ? studentSteps : professionalSteps;

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setIsThinking(true);
      const nextStepIndex = currentStep + 1;
      
      // Physically "scroll" the user to the next section
      setActiveTab(steps[nextStepIndex].targetTab);

      setTimeout(() => {
        setCurrentStep(nextStepIndex);
        setIsThinking(false);
      }, 800);
    } else {
      onComplete();
    }
  };

  return (
    <div className="fixed inset-0 z-[3000] flex items-end md:items-center justify-center p-4 md:p-12 bg-blue-950/20 backdrop-blur-[2px] pointer-events-none">
      <div className="max-w-md w-full bg-white dark:bg-slate-900 rounded-[2.5rem] md:rounded-[3.5rem] shadow-[0_50px_100px_-20px_rgba(0,0,0,0.5)] border border-white dark:border-slate-800 p-8 md:p-10 pointer-events-auto animate-in zoom-in-95 duration-500 relative overflow-hidden mb-20 md:mb-0">
        
        <div className="absolute -top-10 -right-10 w-40 h-40 bg-blue-600/5 rounded-full blur-[40px] animate-pulse"></div>

        <header className="flex items-center gap-5 mb-6 md:mb-8">
          <div className="w-12 h-12 md:w-16 md:h-16 bg-blue-600 text-white rounded-[1.2rem] md:rounded-[1.5rem] flex items-center justify-center text-xl md:text-2xl shadow-xl shadow-blue-600/20 relative">
            {isThinking ? (
              <i className="fas fa-circle-notch animate-spin"></i>
            ) : (
              <i className={`fas ${steps[currentStep].icon}`}></i>
            )}
            <div className="absolute -bottom-1 -right-1 w-4 h-4 md:w-5 md:h-5 bg-emerald-500 rounded-full border-4 border-white dark:border-slate-900"></div>
          </div>
          <div>
            <p className="text-[9px] font-black uppercase tracking-[0.4em] text-blue-600 dark:text-blue-400">Sage Walkthrough</p>
            <h3 className="text-lg font-black text-blue-950 dark:text-white tracking-tighter">Feature {currentStep + 1} of {steps.length}</h3>
          </div>
        </header>

        <div className={`space-y-4 md:space-y-6 transition-all duration-500 ${isThinking ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
          <h2 className="text-2xl md:text-3xl font-black text-blue-950 dark:text-white tracking-tighter leading-none">{steps[currentStep].title}</h2>
          <p className="text-base md:text-lg font-bold text-slate-500 dark:text-slate-400 leading-relaxed italic">
            "{steps[currentStep].content}"
          </p>
        </div>

        <div className="mt-8 md:mt-12 flex items-center justify-between pt-6 md:pt-8 border-t border-slate-100 dark:border-slate-800">
          <button 
            onClick={onComplete}
            className="text-[9px] font-black uppercase tracking-widest text-slate-400 hover:text-red-500 transition-colors"
          >
            Skip Walkthrough
          </button>
          <button 
            onClick={handleNext}
            className="px-6 md:px-10 py-4 md:py-5 bg-blue-950 dark:bg-blue-600 text-white rounded-2xl font-black text-[10px] md:text-[11px] uppercase tracking-widest hover:bg-blue-800 transition shadow-2xl active:scale-95"
          >
            {currentStep === steps.length - 1 ? "Start Exploring" : "Next Feature"}
          </button>
        </div>

        <div className="absolute bottom-0 left-0 h-1.5 bg-blue-100 dark:bg-slate-800 w-full">
           <div 
             className="h-full bg-blue-600 transition-all duration-1000 ease-out shadow-[0_0_15px_rgba(37,99,235,0.5)]"
             style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
           ></div>
        </div>
      </div>
    </div>
  );
};

export default SageGuidedTour;
